import { PaperClipIcon, UserIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { ButtonDropdown } from '@/components/core/ButtonDropdown/ButtonDropdown';
import { HTMLInput } from '@/components/core/HTMLInput';
import { Icon } from '@/components/core/Icon';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/core/Select';
import { Uploader, type UploadItem } from '@/components/core/Uploader';
import { type TaskAttachment as UploadedAttachment,uploadTaskAttachment } from '@/dialogs/create-task/createTask.queries';
import { FormResetProvider } from '@/dialogs/item-editor/Context/FormResetContext';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';

import {
    canReopenTask,
    currentTaskUser,
    type EditorTask,
    transitionLabel,
    userImageUrl,
} from './taskView';
import { saveTask, saveTaskComment } from './taskView.queries';

const MAX_ATTACHMENT_SIZE = 32 * 1024 * 1024;

type TaskWorkflowApi = {
    /**
     * Persists `FINISHED` in one save. `getPatch` supplies unsaved form fields
     * so Close Task does not post the last saved task. Return `null` to abort.
     */
    closeTask: (getPatch?: () => Partial<EditorTask> | null) => void;
    /** Transition and draft checks shared with the close confirmation. */
    validateClose: (getPatch?: () => Partial<EditorTask> | null) => boolean;
    reopenTask: () => void;
    closing: boolean;
    canClose: boolean;
    canReopen: boolean;
};

type TaskViewActionsProps = {
    task: EditorTask;
    onTaskUpdated?: (task: EditorTask) => void;
    onBrowse?: (search: Record<string, string>) => void;
    onWorkflowBind?: (api: TaskWorkflowApi) => void;
};

const hasVisibleText = (html: string): boolean => {
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
};

const CommentAuthorAvatar = () => {
    const user = currentTaskUser();
    const imageUrl = user.imageUrl || userImageUrl(user.userId);

    return (
        <span className="tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-fill-secondary lg:tw-h-10 lg:tw-w-10">
            {imageUrl ? (
                <img src={imageUrl} alt="" className="tw-h-full tw-w-full tw-object-cover" />
            ) : (
                <Icon icon={UserIcon} className="tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6" />
            )}
        </span>
    );
};

const TaskWorkflow = ({ task, onTaskUpdated, onWorkflowBind }: TaskViewActionsProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [chosenTransition, setChosenTransition] = useState(task.chosenTransition ?? '');
    const [saving, setSaving] = useState(false);
    const transitions = task.transitions ?? [];
    const isOpen = task.taskStatus === 'OPEN';
    const showReopen = task.taskStatus === 'FINISHED' && canReopenTask(task);
    const hostOwnsStatusActions = onWorkflowBind != null;
    const showInlineReopen = showReopen && !hostOwnsStatusActions;
    const finishedTransition = task.taskStatus === 'FINISHED' ? transitionLabel(task) : '';

    useEffect(() => {
        setChosenTransition(task.chosenTransition ?? '');
    }, [task.chosenTransition, task.id]);

    const persist = useCallback(async (next: EditorTask, successKey: string) => {
        setSaving(true);
        try {
            const saved = await saveTask(next);
            queryClient.setQueryData(['task', { taskId: task.id }], saved);
            await queryClient.invalidateQueries({ queryKey: ['taskComments'] });
            onTaskUpdated?.(saved);
            toast.success(t(successKey));
        } catch (error) {
            logger.error('Saving task failed', error);
            toast.error(t('ERROR_OCCURRED'));
        } finally {
            setSaving(false);
        }
    }, [onTaskUpdated, queryClient, t, task.id, toast]);

    const validateClose = useCallback((getPatch?: () => Partial<EditorTask> | null) => {
        if (transitions.length > 0 && !chosenTransition) {
            toast.error(t('TASK.CLOSE_TASK.FAILED_MESSAGE'));
            return false;
        }
        const patch = getPatch ? getPatch() : {};
        return patch != null;
    }, [chosenTransition, t, toast, transitions.length]);

    const closeTask = useCallback((getPatch?: () => Partial<EditorTask> | null) => {
        if (!validateClose(getPatch)) {
            return;
        }
        const patch = getPatch ? getPatch() : {};
        if (patch == null) {
            return;
        }
        persist({
            ...task,
            ...patch,
            taskStatus: 'FINISHED',
            chosenTransition: chosenTransition || undefined,
        }, 'TASK.SAVE_TASK_SUCCESS_MESSAGE');
    }, [chosenTransition, persist, task, validateClose]);

    const reopenTask = useCallback(() => {
        persist({ ...task, taskStatus: 'OPEN' }, 'TASK.SAVE_TASK_SUCCESS_MESSAGE');
    }, [persist, task]);

    useEffect(() => {
        onWorkflowBind?.({
            closeTask,
            validateClose,
            reopenTask,
            closing: saving,
            canClose: isOpen,
            canReopen: showReopen,
        });
    }, [closeTask, isOpen, onWorkflowBind, reopenTask, saving, showReopen, validateClose]);

    const showWorkflowRow = (isOpen && transitions.length > 0) || showInlineReopen;

    if (!showWorkflowRow && !finishedTransition) {
        return null;
    }

    return (
        <>
            {showWorkflowRow && (
                <div className="tw-grid tw-grid-cols-1 tw-gap-2 lg:tw-grid-cols-[11rem_minmax(0,1fr)] lg:tw-items-center lg:tw-gap-x-4">
                    <span className="tw-text-t-xs tw-text-t-secondary lg:tw-text-t-sm">
                        {t('TASK.WORKFLOW')}
                    </span>
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                        {isOpen && transitions.length > 0 && (
                            <div className="tw-w-56">
                                <Select value={chosenTransition || undefined} onValueChange={setChosenTransition}>
                                    <SelectTrigger id="task-workflow" aria-label={t('TASK.WORKFLOW')}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {transitions.map((transition) => (
                                            <SelectItem key={transition.key} value={transition.key}>
                                                {transition.text || transition.key}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {showInlineReopen && (
                            <Button id="reopen-task" variant="outline" loading={saving} onClick={reopenTask}>
                                {t('REOPEN_TASK')}
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {finishedTransition && (
                <div className="tw-grid tw-grid-cols-1 tw-gap-2 lg:tw-grid-cols-[11rem_minmax(0,1fr)] lg:tw-items-center lg:tw-gap-x-4">
                    <span className="tw-text-t-xs tw-text-t-secondary lg:tw-text-t-sm">
                        {t('TASK.CHOSEN_TRANSITION')}
                    </span>
                    <span className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">{finishedTransition}</span>
                </div>
            )}
        </>
    );
};

const TaskRelatedItems = ({ task, onBrowse }: TaskViewActionsProps) => {
    const { t } = useTranslation();

    if (!task.itemTag) {
        return null;
    }

    return (
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
            <a
                className="tw-text-t-sm tw-text-t-primary tw-underline lg:tw-text-t-base"
                href={`/browse?tags=${encodeURIComponent(task.itemTag)}`}
                onClick={(event) => {
                    if (!onBrowse) {
                        return;
                    }
                    event.preventDefault();
                    onBrowse({ tags: task.itemTag as string });
                }}
            >
                {t('TASK.SHOW_RELATED_ITEMS')}
            </a>
        </div>
    );
};

const TaskCommentComposer = ({ task }: TaskViewActionsProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [expanded, setExpanded] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);
    const [text, setText] = useState('');
    const [editorKey, setEditorKey] = useState(0);
    const [uploads, setUploads] = useState<UploadItem[]>([]);
    const [attachments, setAttachments] = useState<Record<string, UploadedAttachment[]>>({});
    const [saving, setSaving] = useState(false);

    const setUpload = (id: string, patch: Partial<UploadItem>) => {
        setUploads((current) => current.map((upload) => (upload.id === id ? { ...upload, ...patch } : upload)));
    };

    const startUpload = (upload: UploadItem) => {
        uploadTaskAttachment(upload.file).then((assets) => {
            setAttachments((current) => ({ ...current, [upload.id]: assets }));
            setUpload(upload.id, { status: 'success', progress: 100 });
        }).catch((error) => {
            logger.error('Task comment attachment upload failed', error);
            setUpload(upload.id, { status: 'error' });
        });
    };

    const onFilesAdded = (files: File[]) => {
        const added = files.map((file): UploadItem => ({
            id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
            file,
            status: 'uploading',
            startedAt: Date.now(),
        }));
        setUploads((current) => [...current, ...added]);
        added.forEach(startUpload);
    };

    const onFileRemove = (item: UploadItem) => {
        setUploads((current) => current.filter((upload) => upload.id !== item.id));
        setAttachments((current) => Object.fromEntries(Object.entries(current).filter(([id]) => id !== item.id)));
    };

    useEffect(() => {
        if (!expanded) {
            return;
        }
        sectionRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }, [expanded]);

    const addComment = async () => {
        if (!hasVisibleText(text)) {
            return;
        }
        if (uploads.some((upload) => upload.status !== 'success')) {
            toast.warning(t('NOTIFY.FILES_STILL_NOT_UPLOADED'));
            return;
        }

        setSaving(true);
        try {
            const assets = Object.values(attachments).flat();
            await saveTaskComment(task.id, {
                text,
                author: currentTaskUser().userId,
                ...(assets.length > 0 ? { assets } : {}),
            });
            await queryClient.invalidateQueries({ queryKey: ['taskComments'] });
            setText('');
            setUploads([]);
            setAttachments({});
            setEditorKey((key) => key + 1);
            toast.success(t('SAVE_SUCCESS_MESSAGE'));
        } catch (error) {
            logger.error('Saving task comment failed', error);
            toast.error(t('ERROR_OCCURRED'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-3">
            <ButtonDropdown
                id="add-new-comment"
                variant="ghost"
                titleOne={t('TASK.NEW_COMMENT')}
                isOpen={expanded}
                aria-controls="task-new-comment"
                onClick={() => setExpanded((current) => !current)}
            />
            {expanded && (
                <div ref={sectionRef} id="task-new-comment" className="tw-flex tw-items-start tw-gap-3">
                    <CommentAuthorAvatar />
                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-3 tw-rounded tw-bg-fill-secondary tw-p-4">
                        <FormResetProvider>
                            <HTMLInput
                                key={editorKey}
                                id="task-comment"
                                rows={4}
                                value={text}
                                onChange={setText}
                            />
                        </FormResetProvider>
                        <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                            <Icon icon={PaperClipIcon} className="tw-h-4 tw-w-4 lg:tw-h-5 lg:tw-w-5" />
                            {t('TASK.ATTACHMENT')}
                        </span>
                        <Uploader
                            isMultiple
                            maxFileSize={MAX_ATTACHMENT_SIZE}
                            uploads={uploads}
                            isDisabled={saving}
                            onFilesAdded={onFilesAdded}
                            onFileRemove={onFileRemove}
                            onFileRetry={(item) => {
                                setUpload(item.id, { status: 'uploading', startedAt: Date.now() });
                                startUpload(item);
                            }}
                        />
                        <div className="tw-flex tw-justify-end">
                            <Button
                                id="add-task-comment"
                                loading={saving}
                                disabled={!hasVisibleText(text) || saving}
                                onClick={addComment}
                            >
                                {t('TASK.ADD_COMMENT')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export type { TaskViewActionsProps };
export type { TaskWorkflowApi };
export { TaskCommentComposer, TaskRelatedItems, TaskWorkflow };
